import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StravaConnectButton } from "@/components/strava-connect-button";
import { ImportingProgress } from "@/components/importing-progress";
import { useImportProgress } from "@/api/imports";
import { useListStravaApps } from "@/api/orval";

const LoadingSkeleton = () => (
  <div className="space-y-4 pt-6">
    <Skeleton className="h-8 w-24" />
    <Skeleton className="mb-4 h-3 w-full" />
    <div className="space-y-1">
      <div className="flex justify-between">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="flex justify-between">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  </div>
);

const NoProgress = ({
  totalStreets,
  totalKm,
  isStravaConnected,
}: {
  totalStreets: number;
  totalKm: number;
  isStravaConnected: boolean;
}) => (
  <div className="flex flex-col items-center justify-center gap-3 pt-6">
    <div className="text-lg font-medium">Ready to explore?</div>
    <div className="text-center text-sm text-muted-foreground">
      There are {totalStreets} streets totaling {totalKm.toFixed(1)} km in this
      city.{" "}
      {isStravaConnected
        ? "Head out for a run and it'll start filling in."
        : "Connect Strava and your runs will start filling it in."}
    </div>
    {!isStravaConnected && <StravaConnectButton />}
  </div>
);

export const ProgressCard = () => {
  const { workspace, workspaceId, isWorkspaceLoading } = useCurrentWorkspace();
  const { data: stravaApps } = useListStravaApps();
  const isStravaConnected = (stravaApps?.length ?? 0) > 0;
  const workspaceProgress = workspace?.progress;

  const { progress } = useImportProgress();
  const importing =
    progress !== null &&
    progress.workspaceId === workspaceId &&
    (progress.phase === "importing" || progress.phase === "retrying");

  return (
    <div className="absolute left-4 top-4 w-full max-w-[calc(100vw-32px)] rounded-xl bg-white shadow sm:w-[415px]">
      <Card className="w-full">
        {workspaceProgress && workspaceProgress?.total_matched_streets > 0 && (
          <CardHeader className="pb-2">
            <CardTitle>
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium">Progress</p>
              </div>
            </CardTitle>
          </CardHeader>
        )}
        <CardContent>
          {isWorkspaceLoading ? (
            <LoadingSkeleton />
          ) : importing && progress ? (
            <ImportingProgress
              index={progress.index}
              total={progress.total}
              retrying={progress.phase === "retrying"}
            />
          ) : (
            <>
              {workspaceProgress?.total_matched_streets === 0 ? (
                <NoProgress
                  totalStreets={workspaceProgress?.total_streets}
                  totalKm={workspaceProgress?.total_network_km}
                  isStravaConnected={isStravaConnected}
                />
              ) : (
                <>
                  <div className="mb-2 flex items-baseline">
                    <span className="text-3xl font-bold">
                      {workspaceProgress?.progress_percentage.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    value={workspaceProgress?.progress_percentage}
                    className="mb-4 h-2"
                  />
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Streets</span>
                      <span>
                        {workspaceProgress?.total_matched_streets.toLocaleString()}{" "}
                        / {workspaceProgress?.total_streets.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Distance</span>
                      <span>
                        {(workspaceProgress?.total_matched_km ?? 0).toFixed(1)}{" "}
                        / {workspaceProgress?.total_network_km.toFixed(1)} km
                      </span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
